import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../lib/api';

interface FlagAuditLogProps {
  flagId: string;
}

export default function FlagAuditLog({ flagId }: FlagAuditLogProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'feature-flags', flagId, 'audit-log'],
    queryFn: () => adminApi.getFeatureFlagAuditLog(flagId),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="text-gray-500 text-sm">로딩 중...</span>
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <div className="p-4 bg-red-50 text-red-600 rounded text-sm">
        변경 이력을 불러오는데 실패했습니다.
      </div>
    );
  }

  const entries = data.data || [];

  if (entries.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 text-sm">
        변경 이력이 없습니다.
      </div>
    );
  }

  return (
    <div className="relative">
      {/* 타임라인 세로선 */}
      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

      <div className="space-y-4">
        {entries.map((entry) => (
          <div key={entry.id} className="relative pl-10">
            {/* 점 */}
            <div className="absolute left-2.5 top-1.5 w-3 h-3 rounded-full bg-white border-2 border-gray-400" />

            <div className="bg-gray-50 rounded-card p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-900">
                  {formatAction(entry.action)}
                </span>
                <span className="text-xs text-gray-500">
                  {formatDate(entry.createdAt)}
                </span>
              </div>

              <div className="text-xs text-gray-500 mb-1">
                {entry.context?.actorName || entry.context?.actorEmail || '시스템'}
              </div>

              {entry.oldValue && (
                <div className="mt-2 text-xs text-gray-600">
                  <span className="font-medium">이전: </span>
                  <span className="text-red-500">{formatJson(entry.oldValue)}</span>
                </div>
              )}
              {entry.newValue && (
                <div className="text-xs text-gray-600">
                  <span className="font-medium">변경: </span>
                  <span className="text-green-600">{formatJson(entry.newValue)}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatAction(action: string): string {
  const map: Record<string, string> = {
    FEATURE_FLAG_CREATE: '생성',
    FEATURE_FLAG_UPDATE: '수정',
    FEATURE_FLAG_ENABLE: '활성화',
    FEATURE_FLAG_DISABLE: '비활성화',
    FEATURE_FLAG_ROLLOUT_UPDATE: '롤아웃 변경',
    FEATURE_FLAG_OVERRIDE_CREATE: '오버라이드 추가',
    FEATURE_FLAG_OVERRIDE_DELETE: '오버라이드 삭제',
    FEATURE_FLAG_ARCHIVE: '보관',
    FEATURE_FLAG_HARD_DELETE: '완전 삭제',
    FEATURE_FLAG_RESTORE: '복구',
    FEATURE_FLAG_DELETE: '삭제',
  };
  return map[action] || action;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
}

function formatJson(value: Record<string, unknown>): string {
  const entries = Object.entries(value);
  if (entries.length === 0) return '-';
  return entries
    .map(([k, v]) => `${k}: ${v === null ? '-' : String(v)}`)
    .join(', ');
}
